/**
 * useTTSAlert — Web Speech API TTS para alertas P0 (CRITICAL)
 *
 * Story 1.9.1 (Expansão 2026 — PRD-1 §2.7.1).
 *
 * Mount-once-globally pattern: chamar uma única vez em layout autenticado
 * (ex: <TTSAlertMounter /> dentro de NotificationProvider em App.tsx).
 * Subscreve ao mesmo WS de alertas que `useEquipmentWebSocket` usa
 * (`/ws/equipments`, payload `alert_update` publicado por
 * `alert_scheduler.py:_broadcast_alert_via_websocket`).
 *
 * 3 layers de gating (todos precisam passar):
 *  1. isSupported  — capability do browser (speechSynthesis ausente em alguns
 *     browsers corporativos)
 *  2. opts.enabled — override de código (default true)
 *  3. isUserEnabled — preferência do user gravada em
 *     localStorage["seweb_tts_alerts_enabled"] (default true)
 *
 * AC: Alert P0 toca som no browser.
 *
 * NÃO é feito nesta story:
 *  - gating per-rule fino (precisaria mudar payload WS no backend; out of scope)
 *  - reconexão automática (delegada a outras WS hooks da app)
 *  - UI pra picker de voz (API exposta via opts.voiceURI; UI fica pra futuro)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useTenant } from "@/contexts/TenantContext";
import { getWebSocketUrl } from "@/services/api";

const STORAGE_KEY = "seweb_tts_alerts_enabled";
const AUTH_STORAGE_KEY = "seweb_auth";

export interface UseTTSAlertOptions {
  /** Override de código. Default true. Set false em testes/storybook. */
  enabled?: boolean;
  /** BCP-47 lang. Default "pt-BR". */
  lang?: string;
  /** Rate 0.1..10. Default 0.9 (per PRD §2.7.1). */
  rate?: number;
  /** Volume 0..1. Default 1.0. */
  volume?: number;
  /** Identificador opcional de voz (speechSynthesis.getVoices().voiceURI). */
  voiceURI?: string;
}

export interface UseTTSAlertReturn {
  /**
   * Fala um texto. Retorna true se realmente disparou TTS, false se foi
   * gateado (sem suporte / desligado / opts.enabled=false).
   * Útil pra Daily Briefing (Story 1.9.2) reusar este hook.
   */
  speak: (text: string) => boolean;
  /** Persiste preferência do user no localStorage. */
  setUserEnabled: (enabled: boolean) => void;
  /** Estado atual do toggle do user. */
  isUserEnabled: boolean;
  /** Browser suporta Web Speech API? */
  isSupported: boolean;
}

interface AlertWebSocketPayload {
  type?: string;
  action?: string;
  alert?: {
    id?: string;
    title?: string;
    message?: string;
    severity?: string;
  };
}

function isBrowserTTSSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "speechSynthesis" in window &&
    typeof window.SpeechSynthesisUtterance !== "undefined"
  );
}

function readUserEnabled(): boolean {
  try {
    if (typeof localStorage === "undefined") return true;
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw !== "false";
  } catch {
    return true;
  }
}

function writeUserEnabled(enabled: boolean): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(STORAGE_KEY, enabled ? "true" : "false");
  } catch {
    // localStorage indisponível (ex: modo privado com quota): ignore.
  }
}

function readAuthToken(): string | null {
  try {
    if (typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.access_token ?? null;
  } catch {
    return null;
  }
}

function buildAlertText(alert: AlertWebSocketPayload["alert"]): string {
  const subject = alert?.title?.trim() || alert?.message?.trim();
  if (subject) return `Alerta crítico: ${subject}`;
  return "Novo alerta crítico";
}

export function useTTSAlert(opts: UseTTSAlertOptions = {}): UseTTSAlertReturn {
  const { currentTenant } = useTenant();

  const isSupported = useMemo(() => isBrowserTTSSupported(), []);
  const [isUserEnabled, setIsUserEnabledState] = useState<boolean>(readUserEnabled);

  const wsRef = useRef<WebSocket | null>(null);

  const lang = opts.lang ?? "pt-BR";
  const rate = opts.rate ?? 0.9;
  const volume = opts.volume ?? 1.0;
  const voiceURI = opts.voiceURI;
  const enabledOverride = opts.enabled !== false;

  // Dedup TTS por alert.id — defesa contra múltiplas WS connections entregarem
  // o mesmo alerta. TTL 5s; ids passam por cleanup oportunístico.
  const recentAlertIdsRef = useRef<Map<string, number>>(new Map());
  const DEDUP_TTL_MS = 5_000;

  const speak = useCallback(
    (text: string): boolean => {
      if (!isSupported) return false;
      if (!enabledOverride) return false;
      if (!readUserEnabled()) return false;
      if (!text || !text.trim()) return false;

      try {
        const utter = new SpeechSynthesisUtterance(text);
        utter.lang = lang;
        utter.rate = rate;
        utter.volume = volume;
        if (voiceURI) {
          const match = window.speechSynthesis.getVoices().find((v) => v.voiceURI === voiceURI);
          if (match) utter.voice = match;
        }
        window.speechSynthesis.speak(utter);
        return true;
      } catch {
        return false;
      }
    },
    [isSupported, enabledOverride, lang, rate, volume, voiceURI]
  );

  const setUserEnabled = useCallback((enabled: boolean) => {
    writeUserEnabled(enabled);
    setIsUserEnabledState(enabled);
  }, []);

  useEffect(() => {
    if (!isSupported || !enabledOverride || !currentTenant?.id) return;
    if (typeof WebSocket === "undefined") return;

    let url: URL;
    try {
      url = new URL(getWebSocketUrl("/api/v1/ws/equipments"));
    } catch {
      return;
    }
    url.searchParams.set("company_id", currentTenant.id);
    const token = readAuthToken();
    if (token) url.searchParams.set("token", token);

    let ws: WebSocket;
    try {
      ws = new WebSocket(url.toString());
    } catch {
      return;
    }
    wsRef.current = ws;

    ws.onmessage = (event: MessageEvent) => {
      let payload: AlertWebSocketPayload;
      try {
        payload = JSON.parse(event.data);
      } catch {
        return;
      }
      if (
        payload.type !== "alert_update" ||
        payload.action !== "created" ||
        payload.alert?.severity !== "critical"
      ) {
        return;
      }

      // TODO Story 1.9.x: gate por `payload.alert.tts_enabled` (eco do
      // rule.config.tts_enabled) quando backend incluir no payload — hoje
      // toca pra TODO alerta CRITICAL e operador controla via toggle global.
      const alertId = payload.alert?.id;
      if (alertId) {
        const now = Date.now();
        const recent = recentAlertIdsRef.current;
        const seenAt = recent.get(alertId);
        if (seenAt && now - seenAt < DEDUP_TTL_MS) return;
        recent.set(alertId, now);
        // Limpa entradas antigas oportunísticamente (cap de memória).
        if (recent.size > 50) {
          for (const [id, ts] of recent) {
            if (now - ts >= DEDUP_TTL_MS) recent.delete(id);
          }
        }
      }
      speak(buildAlertText(payload.alert));
    };

    ws.onerror = () => {
      // best-effort: TTS não é crítico — reconexão fica a cargo de outros hooks.
    };

    return () => {
      try {
        ws.close();
      } catch {
        // ignore
      }
      wsRef.current = null;
    };
  }, [currentTenant?.id, isSupported, enabledOverride, speak]);

  return {
    speak,
    setUserEnabled,
    isUserEnabled,
    isSupported,
  };
}

export default useTTSAlert;
